/**
 * NodeRunInputModal — "Visualizar contexto" (spec 2026-09-20 §5.6, D17).
 *
 * Mostra o objeto REAL que o nó recebeu numa execução passada. À esquerda, as
 * execuções em que ele recebeu contexto (o mesmo desenho de status da barra
 * lateral do histórico); à direita, o JSON da passagem escolhida.
 *
 * Camadas: o `Modal` do sistema é `z-[60]`, ABAIXO do painel do nó e do seletor
 * (`z-[200]/[201]`) — por isso `layer="editor"`, e por isso a página fecha os
 * dois ao abrir esta modal. O `Modal` também não fecha ao clicar fora, de
 * construção (não existe handler no fundo) — é o que a spec pede.
 */

import React, { useEffect, useMemo, useState } from 'react';

import {
  WORKFLOW_NODE_INPUT_MAX_BYTES,
  WORKFLOW_NODE_INPUT_RETENTION_DAYS,
} from '@troia-v3/types';
import type { NodeRunInput, NodeRunListItem } from '@troia-v3/types';

import { Button, Modal, ModalContent, ModalHeader, Select, Textarea } from '@/shared/components/ui';
import { useTheme } from '@/contexts/theme';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

import { useNodeRunInputs, useNodeRuns } from '../hooks/useNodeRunInputs';
import {
  EXECUTION_STATUS_COLORS,
  EXECUTION_STATUS_ICONS,
  EXECUTION_STATUS_LABELS,
  formatDuration,
  formatRelativeTime,
} from '../utils/execution-display';
import type { UnrecordedNodeReason } from '../utils/node-envelope-membership';

/** NUNCA #91: o que chega a um nó não é um acumulado de tudo que já rodou. */
const EXPLAINER =
  'O que chega a um nó é a saída do nó anterior, as variáveis e as entidades da execução — não um acumulado de todos os nós.';
const EMPTY_NO_CONTEXT =
  `Nenhuma execução com contexto guardado. O contexto de cada nó é guardado só para execuções novas e fica disponível por ${WORKFLOW_NODE_INPUT_RETENTION_DAYS} dias.`;
const EMPTY_NEVER_RAN = 'Este nó ainda não rodou.';
const UNRECORDED_MESSAGE =
  'Este nó roda como ferramenta do agente (ou é um escopo) — o contexto não é registrado.';
const LOAD_ERROR = 'Não foi possível carregar o contexto deste nó.';
const COPY_ERROR = 'Não foi possível copiar.';
const LOADING_LABEL = 'Carregando…';
/** O teto sai da constante do pacote — nunca "32 KB" escrito à mão (NUNCA #86). */
const MAX_KB = Math.round(WORKFLOW_NODE_INPUT_MAX_BYTES / 1024);

export interface NodeRunInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  nodeId: string;
  /** Rótulo do nó, como aparece no quadro. */
  nodeLabel: string;
  /** Nó que não passa pelo envelope — a modal explica e não busca nada. */
  unrecordedReason: UnrecordedNodeReason | null;
}

export const NodeRunInputModal: React.FC<NodeRunInputModalProps> = ({
  isOpen,
  onClose,
  workflowId,
  nodeId,
  nodeLabel,
  unrecordedReason,
}) => {
  const { lightTokens } = useTheme();
  const lt = lightTokens.colors;

  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [passIndex, setPassIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const canFetch = isOpen && unrecordedReason === null;
  const runs = useNodeRuns(workflowId, nodeId, { enabled: canFetch });
  const inputs = useNodeRunInputs(selectedExecutionId, nodeId, {
    enabled: canFetch && selectedExecutionId !== null,
  });

  const items = useMemo<NodeRunListItem[]>(() => runs.data?.items ?? [], [runs.data]);
  const passes = useMemo<NodeRunInput[]>(() => inputs.data?.items ?? [], [inputs.data]);

  // Escolhe a primeira execução assim que a lista chega — e conserta a escolha
  // se a lista mudar e a execução escolhida não estiver mais nela.
  useEffect(() => {
    if (items.length === 0) {
      setSelectedExecutionId(null);
      return;
    }
    const stillThere = items.some((item) => item.executionId === selectedExecutionId);
    if (!stillThere) setSelectedExecutionId(items[0]?.executionId ?? null);
  }, [items, selectedExecutionId]);

  // Trocar de execução recomeça na primeira passagem.
  useEffect(() => {
    setPassIndex(0);
    setCopied(false);
    setCopyFailed(false);
  }, [selectedExecutionId]);

  // Trocar de PASSAGEM troca o conteúdo da tela do mesmo jeito: o aviso da
  // tentativa anterior, ao lado de um JSON que já não é o mesmo, viraria
  // mentira ("não foi possível copiar" o quê?).
  useEffect(() => {
    setCopied(false);
    setCopyFailed(false);
  }, [passIndex]);

  const safePassIndex = passes.length > 0 ? Math.min(passIndex, passes.length - 1) : 0;
  const currentPass = passes.length > 0 ? passes[safePassIndex] : undefined;

  /**
   * O aviso do teto de passagens, ou `null` quando não há o que avisar.
   *
   * `item.passes` é o total VERDADEIRO de passagens da execução; o que chega
   * aqui é o que o servidor entrega de uma vez. Quando o total é maior, a tela
   * diz quantas está mostrando — sem isso, o item da esquerda anuncia um número
   * e o seletor ao lado oferece outro, menor, sem nenhuma explicação.
   */
  const passCapNotice = useMemo<string | null>(() => {
    if (passes.length === 0) return null;
    const selectedRun = items.find((item) => item.executionId === selectedExecutionId);
    if (selectedRun === undefined || selectedRun.passes <= passes.length) return null;
    return `Mostrando as primeiras ${passes.length} de ${selectedRun.passes} passagens.`;
  }, [items, passes.length, selectedExecutionId]);
  const jsonText = useMemo(
    () => (currentPass === undefined ? '' : JSON.stringify(currentPass.input ?? null, null, 2)),
    [currentPass],
  );

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      setCopyFailed(false);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // `navigator.clipboard` só existe em contexto seguro (https, ou
      // localhost) e ainda pode ser recusado por permissão do navegador.
      // Falhar calado é o pior desfecho: o botão continua dizendo "Copiar",
      // quem clicou acha que copiou e cola outra coisa.
      setCopied(false);
      setCopyFailed(true);
    }
  };

  /**
   * As passagens da execução escolhida ainda estão vindo.
   *
   * Sem isto, a Textarea aparece VAZIA enquanto a busca acontece — e Textarea
   * vazia nesta modal quer dizer exatamente a outra coisa ("não há contexto"),
   * que é a mensagem dos estados vazios logo ao lado. Também cobre o instante
   * em que a lista já chegou mas o efeito ainda não escolheu a execução: aí
   * nenhuma busca está no ar e mesmo assim não há nada para mostrar.
   */
  const inputsLoading =
    selectedExecutionId === null
    || inputs.isLoading
    || (inputs.isFetching && currentPass === undefined);

  let listState: 'unrecorded' | 'error' | 'loading' | 'empty' | 'ready' = 'ready';
  if (unrecordedReason !== null) listState = 'unrecorded';
  else if (runs.isError) listState = 'error';
  else if (runs.isLoading) listState = 'loading';
  else if (items.length === 0) listState = 'empty';

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="5xl" layer="editor">
      <div
        data-testid="node-run-input-modal"
        style={{
          // O override claro do `Modal` redefine 22 variáveis (contadas no
          // bloco "Override CSS variables to light theme" de `Modal.tsx`), mas NÃO as três
          // cores de status usadas pelo selo da lista. Sem estas linhas, num
          // tema escuro o selo puxaria a cor do tema ATUAL dentro de uma modal
          // que é sempre clara. Mesma fonte que o `Modal` usa — nada de hex.
          '--theme-success-color': lt.success,
          '--theme-warning-color': lt.warning,
          '--theme-info-color': lt.info,
        } as React.CSSProperties}
      >
        <ModalHeader title={`Visualizar contexto — ${nodeLabel}`} onClose={onClose} />
        <ModalContent>
          <p className="text-xs mb-3" style={{ color: 'var(--theme-muted-text)' }}>
            {EXPLAINER}
          </p>

          {listState === 'unrecorded' && (
            <p className="text-sm py-6 text-center" data-testid="node-run-empty" style={{ color: 'var(--theme-muted-text)' }}>
              {UNRECORDED_MESSAGE}
            </p>
          )}

          {listState === 'error' && (
            <p className="text-sm py-6 text-center" data-testid="node-run-error" style={{ color: 'var(--theme-error-color)' }}>
              {LOAD_ERROR}
            </p>
          )}

          {listState === 'loading' && (
            <p className="text-sm py-6 text-center" data-testid="node-run-loading" style={{ color: 'var(--theme-muted-text)' }}>
              {LOADING_LABEL}
            </p>
          )}

          {listState === 'empty' && (
            <p className="text-sm py-6 text-center" data-testid="node-run-empty" style={{ color: 'var(--theme-muted-text)' }}>
              {runs.data?.workflowHasExecutions === true ? EMPTY_NO_CONTEXT : EMPTY_NEVER_RAN}
            </p>
          )}

          {listState === 'ready' && (
            <div className="flex flex-col md:flex-row gap-4">
              <div
                className="md:w-64 md:flex-none flex flex-col gap-2 md:max-h-[60vh] md:overflow-y-auto md:pr-3 md:border-r"
                style={{ borderColor: 'var(--theme-card-border)' }}
                data-testid="node-run-list"
              >
                {items.map((item) => {
                  const statusIcon = (EXECUTION_STATUS_ICONS[item.status] || 'clock') as IconName;
                  const statusColor = EXECUTION_STATUS_COLORS[item.status] || 'var(--theme-muted-text)';
                  const statusLabel = EXECUTION_STATUS_LABELS[item.status] || item.status;
                  const isSelected = item.executionId === selectedExecutionId;
                  const meta = [
                    item.durationMs !== undefined ? formatDuration(item.durationMs) : null,
                    item.passes > 1 ? `${item.passes} passagens` : null,
                  ].filter((part): part is string => part !== null);

                  return (
                    <button
                      key={item.executionId}
                      type="button"
                      data-testid={`node-run-item-${item.executionId}`}
                      onClick={() => setSelectedExecutionId(item.executionId)}
                      className="w-full rounded-[10px] border p-2.5 text-left transition-colors"
                      style={{
                        borderColor: isSelected ? 'var(--theme-primary)' : 'var(--theme-card-border)',
                        backgroundColor: isSelected
                          ? 'color-mix(in srgb, var(--theme-primary) 10%, transparent)'
                          : 'transparent',
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[11px] font-bold flex-none"
                          style={{
                            color: statusColor,
                            backgroundColor: `color-mix(in srgb, ${statusColor} 13%, transparent)`,
                          }}
                        >
                          <Icon name={statusIcon} className="w-3 h-3" />
                          {statusLabel}
                        </span>
                        {item.isTest && (
                          <span
                            data-testid={`node-run-test-badge-${item.executionId}`}
                            className="h-5 px-2 rounded-full text-[11px] font-bold flex-none inline-flex items-center"
                            style={{
                              color: 'var(--theme-muted-text)',
                              backgroundColor: 'color-mix(in srgb, var(--theme-muted-text) 13%, transparent)',
                            }}
                          >
                            Teste
                          </span>
                        )}
                        <span
                          className="ml-auto text-[11px] tabular-nums flex-none"
                          style={{ color: 'var(--theme-muted-text)' }}
                        >
                          {formatRelativeTime(item.startedAt)}
                        </span>
                      </div>
                      {meta.length > 0 && (
                        <p className="mt-1 text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
                          {meta.join(' · ')}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    {passes.length > 1 && (
                      <Select
                        data-testid="node-run-pass-select"
                        options={passes.map((_, index) => ({
                          value: String(index),
                          label: `Passagem ${index + 1}`,
                        }))}
                        value={String(safePassIndex)}
                        onChange={(value) => setPassIndex(Number(value))}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-none">
                    {copyFailed && (
                      <span
                        className="text-xs"
                        data-testid="node-run-copy-error"
                        style={{ color: 'var(--theme-error-color)' }}
                      >
                        {COPY_ERROR}
                      </span>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={copied ? 'check' : 'copy'}
                      disabled={currentPass === undefined}
                      onClick={() => { void handleCopy(); }}
                      data-testid="node-run-copy"
                    >
                      {copied ? 'Copiado!' : 'Copiar'}
                    </Button>
                  </div>
                </div>

                {/* Erro das PASSAGENS: id próprio porque o erro da LISTA é
                    OUTRO estado, com a mesma frase — e um exclui o outro
                    (`listState` é um valor só: lista que falhou nem monta este
                    lado). Com um id compartilhado, o spec que mira este casa
                    com o da lista e não consegue afirmar "a lista veio inteira
                    e só as passagens falharam". */}
                {inputs.isError && (
                  <p className="text-sm mb-2" data-testid="node-run-input-error" style={{ color: 'var(--theme-error-color)' }}>
                    {LOAD_ERROR}
                  </p>
                )}

                {/* O servidor entrega as PRIMEIRAS passagens; a lista à esquerda
                    anuncia o total verdadeiro. Quando um é maior que o outro, a
                    tela diz — senão o seletor curto ao lado de "N passagens"
                    parece perda de dado. Os dois números vêm dos dados. */}
                {passCapNotice !== null && (
                  <p className="text-xs mb-2" data-testid="node-run-pass-cap" style={{ color: 'var(--theme-muted-text)' }}>
                    {passCapNotice}
                  </p>
                )}

                {currentPass?.truncated === true && (
                  <p className="text-xs mb-2" data-testid="node-run-truncated" style={{ color: 'var(--theme-muted-text)' }}>
                    {`Conteúdo reduzido para caber em ${MAX_KB} KB (tamanho original: ${Math.round(currentPass.originalBytes / 1024)} KB).`}
                  </p>
                )}

                {/* Ou a espera, ou o JSON — nunca a Textarea vazia no meio da
                    busca, que na tela se lê como "este nó não tem contexto". */}
                {inputsLoading && !inputs.isError ? (
                  <p
                    className="text-sm py-6 text-center"
                    data-testid="node-run-input-loading"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {LOADING_LABEL}
                  </p>
                ) : (
                  <Textarea
                    readOnly
                    rows={18}
                    resize="none"
                    className="font-mono text-xs"
                    value={jsonText}
                    data-testid="node-run-json"
                  />
                )}
              </div>
            </div>
          )}
        </ModalContent>
      </div>
    </Modal>
  );
};

NodeRunInputModal.displayName = 'NodeRunInputModal';

export default NodeRunInputModal;
